import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getAuthToken } from './AuthContext';

interface SocketContextType {
    socket: Socket | null;
    connected: boolean;
    on: (event: string, handler: (data: any) => void) => void;
    off: (event: string, handler?: (data: any) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        const token = getAuthToken();
        if (!token) {
            setSocket(null);
            return;
        }

        const newSocket = io(API_HOST, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
        });

        newSocket.on('connect', () => setConnected(true));
        newSocket.on('disconnect', () => setConnected(false));
        newSocket.on('connect_error', (err) => {
            console.warn('[WS] Error de conexión:', err.message);
            setConnected(false);
        });

        setSocket(newSocket);

        return () => {
            newSocket.close();
            setSocket(null);
            setConnected(false);
        };
    }, []);

    const on = useCallback((event: string, handler: (data: any) => void) => {
        if (socket) socket.on(event, handler);
    }, [socket]);

    const off = useCallback((event: string, handler?: (data: any) => void) => {
        if (socket) socket.off(event, handler);
    }, [socket]);

    return (
        <SocketContext.Provider value={{ socket, connected, on, off }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = (): SocketContextType => {
    const ctx = useContext(SocketContext);
    if (!ctx) throw new Error('useSocket debe usarse dentro de SocketProvider');
    return ctx;
};